import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    motion,
    useScroll,
    useSpring,
    useTransform,
} from "motion/react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiArrowRight, FiLayers } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import ClientCalling from "../../api/ClientCalling";
import { formatView } from "../formatView";
import SmartImage from "./SmartImage";

const CardSkeleton = ({ big = false }) => (
    <div
        className={`${
            big
                ? "col-span-2 row-span-2 md:col-span-1 md:row-span-1"
                : "col-span-1 row-span-1"
        } rounded-2xl ios-shimmer relative aspect-[5/6] overflow-hidden`}
    >
        <div
            className='absolute top-2 left-2 w-10 h-4 rounded-full'
            style={{ background: "rgba(255,255,255,0.08)" }}
        />
        <div className='absolute inset-x-2 bottom-2 hidden md:block'>
            <div
                className='h-9 rounded-lg'
                style={{
                    background: "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(8px)",
                }}
            />
        </div>
    </div>
);

const CategorySkeleton = () => (
    <div className='mb-10 md:mb-14'>
        <div className='flex items-end justify-between mb-4 md:mb-5'>
            <div className='flex items-start gap-3'>
                <div className='w-10 h-10 md:w-12 md:h-12 rounded-2xl ios-shimmer flex-shrink-0' />
                <div>
                    <div className='h-5 md:h-7 w-40 md:w-56 rounded-md ios-shimmer mb-2' />
                    <div className='h-3 w-32 md:w-44 rounded-md ios-shimmer opacity-60' />
                </div>
            </div>
            <div className='h-9 w-24 rounded-full ios-shimmer' />
        </div>
        <div className='grid grid-cols-4 md:grid-cols-5 gap-2 md:gap-3 auto-rows-fr'>
            <CardSkeleton big />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
        </div>
    </div>
);

const ItemCard = ({ item, big, index, onClick }) => {
    const positionClass = big
        ? "col-span-2 row-span-2 md:col-span-1 md:row-span-1"
        : "col-span-1 row-span-1";

    return (
        <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.94 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{
                duration: 0.55,
                delay: Math.min(index * 0.06, 0.3),
                ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={{ y: -6, scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={onClick}
            className={`${positionClass} relative aspect-[5/6] rounded-2xl cursor-pointer group bg-client-back`}
            style={{
                overflow: "clip",
                boxShadow:
                    "0 10px 26px -10px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)",
            }}
        >
            <SmartImage
                src={item.l_image}
                alt={item.l_name}
                className='absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
            />
            <div
                className='absolute inset-x-0 bottom-0 h-3/5 pointer-events-none'
                style={{
                    background:
                        "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 40%, transparent 100%)",
                }}
            />
            <div className='absolute top-2 left-2 ios-glass-dark px-2 py-0.5 rounded-full flex items-center gap-1 text-white text-[9px] md:text-[11px] font-medium pointer-events-none'>
                <FiEye className='text-[10px] md:text-xs' />
                <span className='tabular-nums'>{formatView(item.l_view)}</span>
            </div>
            <div className='absolute inset-x-0 bottom-0 p-2 md:p-3 flex items-center justify-between gap-2'>
                <p
                    className='text-white text-[10px] md:text-xs font-semibold truncate flex-1'
                    style={{ textShadow: "0 1px 3px rgba(0,0,0,0.85)" }}
                >
                    {item.l_name}
                </p>
                <div className='ios-glass-btn hidden md:flex px-2.5 py-1 rounded-full text-[9px] md:text-[10px] font-medium text-white items-center gap-1 whitespace-nowrap'>
                    Get Prompt
                </div>
            </div>
            <div
                className='absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300'
                style={{
                    boxShadow:
                        "inset 0 0 0 2px rgba(127,0,255,0.5), 0 0 28px rgba(127,0,255,0.35)",
                }}
            />
        </motion.div>
    );
};

const CategoryBlock = ({ cat, navigate, blockIndex, onInViewChange }) => {
    const sectionRef = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                const isInView = entries[0].isIntersecting;
                setInView(isInView);
                if (isInView) onInViewChange?.(blockIndex);
            },
            { rootMargin: "-25% 0px -55% 0px" }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [blockIndex, onInViewChange]);

    return (
        <motion.div
            ref={sectionRef}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className='relative mb-10 md:mb-16 lg:pl-20'
        >
            {/* Timeline node (lg+) */}
            <div className='hidden lg:block absolute left-0 top-2 z-10'>
                <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ type: "spring", stiffness: 240, damping: 18 }}
                    className='relative w-11 h-11 rounded-full flex items-center justify-center'
                    style={{
                        background: inView
                            ? "linear-gradient(135deg, #7F00FF, #FF8AFF)"
                            : "rgba(18,18,18,0.7)",
                        border: inView
                            ? "1px solid rgba(255,255,255,0.28)"
                            : "1px solid rgba(255,255,255,0.12)",
                        backdropFilter: "blur(14px)",
                        boxShadow: inView
                            ? "0 10px 24px -6px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.22)"
                            : "0 6px 16px -6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)",
                        transition:
                            "background 0.4s, border 0.4s, box-shadow 0.4s",
                    }}
                >
                    {inView && (
                        <motion.span
                            animate={{
                                scale: [1, 1.7, 1],
                                opacity: [0.65, 0, 0.65],
                            }}
                            transition={{ duration: 1.8, repeat: Infinity }}
                            className='absolute inset-0 rounded-full pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, rgba(127,0,255,0.55), transparent 70%)",
                            }}
                        />
                    )}
                    <span
                        className='relative text-xs font-bold tabular-nums'
                        style={{
                            color: inView ? "#fff" : "rgba(255,255,255,0.55)",
                        }}
                    >
                        {String(blockIndex + 1).padStart(2, "0")}
                    </span>
                </motion.div>

                {/* Connector line from node to header */}
                <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className='absolute left-11 top-1/2 -translate-y-1/2 w-7 h-px origin-left'
                    style={{
                        background: inView
                            ? "linear-gradient(90deg, rgba(127,0,255,0.6), rgba(255,255,255,0.1))"
                            : "rgba(255,255,255,0.08)",
                        transition: "background 0.4s",
                    }}
                />
            </div>

            {/* Header */}
            <div className='flex items-end justify-between gap-3 mb-4 md:mb-5'>
                <div className='flex items-start gap-3 min-w-0'>
                    <motion.div
                        initial={{ scale: 0, rotate: -90 }}
                        whileInView={{ scale: 1, rotate: 0 }}
                        viewport={{ once: true }}
                        transition={{ type: "spring", stiffness: 200, damping: 18 }}
                        className='lg:hidden ios-glass w-10 h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center flex-shrink-0'
                        style={{ color: "#FCFA02" }}
                    >
                        <HiSparkles className='text-base md:text-xl' />
                    </motion.div>
                    <div className='min-w-0'>
                        <motion.h2
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.05 }}
                            className='text-base md:text-2xl font-bold tracking-tight text-client-font truncate'
                        >
                            <span className='md:hidden'>
                                {cat.category_name?.length > 22
                                    ? cat.category_name.slice(0, 22) + "…"
                                    : cat.category_name}
                            </span>
                            <span className='hidden md:inline'>
                                {cat.category_name}
                            </span>
                        </motion.h2>
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.15 }}
                            className='flex flex-wrap items-center gap-2 mt-1'
                        >
                            <span className='text-[11px] md:text-xs text-client-font/55'>
                                {cat?.totalList} Avatars
                            </span>
                            <span className='ios-glass px-2 py-0.5 rounded-full flex items-center gap-1 text-[10px] md:text-[11px] text-client-font/75'>
                                <FiEye className='text-[10px]' />
                                <span className='tabular-nums'>
                                    {formatView(cat?.totalview)}
                                </span>
                            </span>
                        </motion.div>
                    </div>
                </div>
                <motion.button
                    onClick={() => navigate(`/category/${cat?._id}`)}
                    whileHover={{ scale: 1.05, x: 2 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className='ios-glass-btn shrink-0 px-3 md:px-4 py-2 rounded-full text-[10px] md:text-xs font-medium text-client-font flex items-center gap-1.5'
                >
                    See all <FiArrowRight className='text-xs' />
                </motion.button>
            </div>

            {/* Grid */}
            <div className='grid grid-cols-4 md:grid-cols-5 gap-2 md:gap-3 auto-rows-fr'>
                {cat.listData.slice(0, 5).map((item, idx) => (
                    <ItemCard
                        key={item._id}
                        item={item}
                        big={idx === 0}
                        index={idx}
                        onClick={() => navigate(`/list/${item._id}`)}
                    />
                ))}
            </div>
        </motion.div>
    );
};

const ClientHomeCategory = () => {
    const [loader, setLoader] = useState(false);
    const [page, setPage] = useState(1);
    const [catData, setCatData] = useState([]);
    const [pinData, setPinData] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const navigate = useNavigate();
    const observerTarget = useRef(null);
    const isFetchingRef = useRef(false);
    const containerRef = useRef(null);

    // Scroll progress along the categories section
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start 65%", "end 80%"],
    });
    const fillScale = useSpring(scrollYProgress, {
        stiffness: 200,
        damping: 30,
        mass: 0.5,
    });

    // Scroll progress for the bg layer (broader range so bg reacts earlier)
    const { scrollYProgress: bgProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    });
    const bgSpring = useSpring(bgProgress, {
        stiffness: 90,
        damping: 30,
        mass: 0.6,
    });
    // Three drifting orbs whose Y and opacity shift with scroll
    const orb1Y = useTransform(bgSpring, [0, 1], ["-10%", "60%"]);
    const orb2Y = useTransform(bgSpring, [0, 1], ["40%", "-20%"]);
    const orb3Y = useTransform(bgSpring, [0, 1], ["80%", "20%"]);
    const orb1X = useTransform(bgSpring, [0, 1], ["-15%", "5%"]);
    const orb2X = useTransform(bgSpring, [0, 1], ["80%", "65%"]);
    const orb3X = useTransform(bgSpring, [0, 1], ["20%", "40%"]);
    // Tint hue shifts subtly as you scroll
    const tintHue = useTransform(
        bgSpring,
        [0, 0.5, 1],
        [
            "linear-gradient(180deg, rgba(127,0,255,0.06) 0%, rgba(127,0,255,0.02) 100%)",
            "linear-gradient(180deg, rgba(255,138,255,0.05) 0%, rgba(127,0,255,0.05) 100%)",
            "linear-gradient(180deg, rgba(252,250,2,0.04) 0%, rgba(255,138,255,0.05) 100%)",
        ]
    );

    const fetchData = useCallback(async (pageNum = 1) => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        try {
            if (pageNum === 1) setLoader(true);
            else setIsLoadingMore(true);

            const resp = await ClientCalling.getCategoryClient(pageNum, 4);
            if (resp?.status) {
                if (pageNum === 1) setPinData(resp?.pinData || []);
                setCatData((prev) =>
                    pageNum === 1
                        ? resp?.data || []
                        : [...prev, ...(resp?.data || [])]
                );
                setNextPage(resp.nextPage);
            }
        } catch (error) {
            console.error("Category fetch failed:", error);
        } finally {
            setLoader(false);
            setIsLoadingMore(false);
            setInitialLoad(false);
            isFetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchData(1);
    }, [fetchData]);

    useEffect(() => {
        const el = observerTarget.current;
        if (!el || !nextPage) return;
        const observer = new IntersectionObserver(
            (entries) => {
                const [target] = entries;
                if (target.isIntersecting && nextPage && !isFetchingRef.current) {
                    setPage(nextPage);
                    fetchData(nextPage);
                }
            },
            { root: null, rootMargin: "300px", threshold: 0 }
        );
        observer.observe(el);
        return () => observer.unobserve(el);
    }, [nextPage, fetchData]);

    if (initialLoad && loader) {
        return (
            <div className='w-full px-3 sm:px-4 my-6 md:my-10'>
                <div className='mx-auto w-full max-w-7xl'>
                    {[...Array(3)].map((_, i) => (
                        <CategorySkeleton key={i} />
                    ))}
                </div>
            </div>
        );
    }

    if ((!pinData || pinData.length === 0) && (!catData || catData.length === 0))
        return null;

    const allCategories = [...(pinData || []), ...(catData || [])];

    return (
        <section className='relative w-full px-3 sm:px-4 my-6 md:my-12 overflow-hidden'>
            {/* === Scroll-reactive ambient background === */}
            <motion.div
                aria-hidden
                className='absolute inset-0 pointer-events-none -z-10'
                style={{ background: tintHue }}
            />
            <motion.div
                aria-hidden
                className='absolute pointer-events-none -z-10 w-[28rem] h-[28rem] rounded-full blur-3xl'
                style={{
                    top: orb1Y,
                    left: orb1X,
                    opacity: 0.32,
                    background:
                        "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                }}
            />
            <motion.div
                aria-hidden
                className='absolute pointer-events-none -z-10 w-[24rem] h-[24rem] rounded-full blur-3xl'
                style={{
                    top: orb2Y,
                    left: orb2X,
                    opacity: 0.22,
                    background:
                        "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                }}
            />
            <motion.div
                aria-hidden
                className='absolute pointer-events-none -z-10 w-[22rem] h-[22rem] rounded-full blur-3xl'
                style={{
                    top: orb3Y,
                    left: orb3X,
                    opacity: 0.2,
                    background:
                        "radial-gradient(circle, #FF8AFF 0%, transparent 70%)",
                }}
            />
            {/* Subtle grid pattern overlay */}
            <div
                aria-hidden
                className='absolute inset-0 pointer-events-none -z-10 opacity-[0.04]'
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                    backgroundSize: "60px 60px",
                    maskImage:
                        "radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 80%)",
                    WebkitMaskImage:
                        "radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 80%)",
                }}
            />

            {/* === SECTION INTRO === */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className='mx-auto w-full max-w-7xl mt-6 md:mt-6 lg:mt-12 mb-8 md:mb-12 lg:pl-20'
            >
                {/* Mobile: single segmented chip combining eyebrow + count */}
                <div
                    className='md:hidden inline-flex items-stretch rounded-full mb-3 overflow-hidden'
                    style={{
                        background: "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(14px) saturate(180%)",
                        WebkitBackdropFilter: "blur(14px) saturate(180%)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        boxShadow:
                            "0 8px 20px -8px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12)",
                    }}
                >
                    <div className='inline-flex items-center gap-1.5 px-3 py-1.5'>
                        <HiSparkles
                            className='text-[11px]'
                            style={{ color: "#FCFA02" }}
                        />
                        <span className='text-client-font/85 text-[10px] uppercase tracking-[0.18em] font-semibold'>
                            Explore
                        </span>
                    </div>
                    <div
                        className='w-px'
                        style={{ background: "rgba(255,255,255,0.12)" }}
                    />
                    <div
                        className='inline-flex items-center gap-1.5 px-3 py-1.5'
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(127,0,255,0.22), rgba(252,250,2,0.06))",
                        }}
                    >
                        <FiLayers
                            className='text-[11px]'
                            style={{ color: "#FCFA02" }}
                        />
                        <span className='text-client-font font-bold text-xs tabular-nums'>
                            {allCategories.length}
                        </span>
                        <span className='text-client-font/65 text-[10px] uppercase tracking-wider'>
                            Collections
                        </span>
                    </div>
                </div>

                {/* Desktop eyebrow (md+) */}
                <div className='hidden md:inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85 mb-3'>
                    <HiSparkles style={{ color: "#FCFA02" }} />
                    <span className='tracking-wider uppercase'>
                        Explore
                    </span>
                </div>

                <div className='flex flex-col md:flex-row md:items-end md:justify-between gap-4'>
                    <div>
                        <h2 className='text-2xl md:text-4xl lg:text-5xl font-bold text-client-font tracking-tight leading-tight'>
                            All{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                Categories
                            </span>
                        </h2>
                        <p className='text-client-font/55 text-sm md:text-base mt-2 max-w-xl'>
                            Scroll through every collection — fresh styles, themes, and
                            ideas updated daily.
                        </p>
                    </div>
                    {/* Desktop-only counter pill */}
                    <div
                        className='hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-full self-start md:self-auto'
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(127,0,255,0.18), rgba(252,250,2,0.04))",
                            border: "1px solid rgba(127,0,255,0.3)",
                        }}
                    >
                        <FiLayers
                            className='text-xs'
                            style={{ color: "#FCFA02" }}
                        />
                        <span className='text-client-font font-bold text-sm tabular-nums'>
                            {allCategories.length}
                        </span>
                        <span className='text-client-font/65 text-[11px] uppercase tracking-wider'>
                            Collections
                        </span>
                    </div>
                </div>
            </motion.div>

            <div
                ref={containerRef}
                className='relative mx-auto w-full max-w-7xl'
            >
                {/* Scroll-fill timeline rail (lg+) */}
                <div
                    aria-hidden
                    className='hidden lg:block absolute left-[22px] top-2 bottom-2 w-0.5 rounded-full pointer-events-none'
                    style={{ background: "rgba(255,255,255,0.06)" }}
                >
                    {/* Gradient fill that follows scroll */}
                    <motion.div
                        className='absolute inset-x-0 top-0 origin-top rounded-full'
                        style={{
                            scaleY: fillScale,
                            background:
                                "linear-gradient(180deg, #7F00FF, #FF8AFF 50%, #FCFA02)",
                            boxShadow:
                                "0 0 12px rgba(127,0,255,0.65), 0 0 24px rgba(127,0,255,0.4)",
                            height: "100%",
                        }}
                    />

                    {/* Floating "you are here" comet on the rail */}
                    <motion.div
                        className='absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full'
                        style={{
                            top: fillScale,
                            translateY: "-50%",
                            background:
                                "radial-gradient(circle, #fff 0%, #FCFA02 40%, #FF8AFF 80%)",
                            boxShadow:
                                "0 0 12px #FCFA02, 0 0 24px #FF8AFF, 0 0 36px rgba(127,0,255,0.6)",
                        }}
                    >
                        <motion.span
                            animate={{
                                scale: [1, 2, 1],
                                opacity: [0.6, 0, 0.6],
                            }}
                            transition={{
                                duration: 1.8,
                                repeat: Infinity,
                                ease: "easeOut",
                            }}
                            className='absolute inset-0 rounded-full'
                            style={{
                                background:
                                    "radial-gradient(circle, rgba(252,250,2,0.6), transparent 70%)",
                            }}
                        />
                    </motion.div>
                </div>

                {/* Floating "now viewing" pill (lg+) — sticks to top while scrolling */}
                {allCategories[activeIndex] && (
                    <div className='hidden lg:block sticky top-4 z-30 pointer-events-none mb-4'>
                        <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            key={activeIndex}
                            className='inline-flex items-center gap-2 px-3 py-1.5 rounded-full ml-12'
                            style={{
                                background: "rgba(18,18,18,0.75)",
                                backdropFilter: "blur(16px) saturate(180%)",
                                WebkitBackdropFilter: "blur(16px) saturate(180%)",
                                border: "1px solid rgba(255,255,255,0.12)",
                                boxShadow:
                                    "0 10px 24px -8px rgba(127,0,255,0.4), inset 0 1px 0 rgba(255,255,255,0.14)",
                            }}
                        >
                            <span
                                className='inline-flex w-1.5 h-1.5 rounded-full'
                                style={{
                                    background: "#FCFA02",
                                    boxShadow: "0 0 6px #FCFA02",
                                }}
                            />
                            <span className='text-client-font/55 text-[10px] uppercase tracking-[0.2em] font-semibold'>
                                Now viewing
                            </span>
                            <span className='text-client-font text-xs font-bold truncate max-w-[180px]'>
                                {allCategories[activeIndex]?.category_name}
                            </span>
                            <span className='text-client-font/45 text-[10px] tabular-nums'>
                                {activeIndex + 1}/{allCategories.length}
                            </span>
                        </motion.div>
                    </div>
                )}

                {allCategories.map((cat, i) => (
                    <CategoryBlock
                        key={cat._id}
                        cat={cat}
                        navigate={navigate}
                        blockIndex={i}
                        onInViewChange={setActiveIndex}
                    />
                ))}

                {/* Infinite scroll trigger */}
                {nextPage && (
                    <div
                        ref={observerTarget}
                        className='flex justify-center items-center py-8 md:py-12 lg:pl-20'
                    >
                        {isLoadingMore ? (
                            <div className='ios-glass-dark inline-flex items-center gap-3 px-5 py-3 rounded-full'>
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{
                                        duration: 0.9,
                                        repeat: Infinity,
                                        ease: "linear",
                                    }}
                                    className='w-4 h-4 rounded-full border-2 border-client-font/30 border-t-client-primary'
                                />
                                <span className='text-client-font text-xs md:text-sm font-medium tracking-wide'>
                                    Loading more categories
                                </span>
                            </div>
                        ) : (
                            <div className='w-full max-w-7xl'>
                                <CategorySkeleton />
                            </div>
                        )}
                    </div>
                )}

                {/* End of list */}
                {!nextPage && allCategories.length > 0 && !initialLoad && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className='flex flex-col items-center gap-2 py-12 text-client-font/45 lg:pl-20'
                    >
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                                duration: 12,
                                repeat: Infinity,
                                ease: "linear",
                            }}
                            className='w-10 h-10 rounded-full flex items-center justify-center'
                            style={{
                                background:
                                    "conic-gradient(from 0deg, #7F00FF, #FF8AFF, #FCFA02, #7F00FF)",
                                padding: 2,
                            }}
                        >
                            <div
                                className='w-full h-full rounded-full flex items-center justify-center'
                                style={{ background: "rgba(10,8,16,0.92)" }}
                            >
                                <HiSparkles
                                    className='text-base'
                                    style={{ color: "#FCFA02" }}
                                />
                            </div>
                        </motion.div>
                        <p className='text-xs md:text-sm tracking-[0.2em] uppercase font-semibold'>
                            You've seen it all
                        </p>
                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default ClientHomeCategory;
